// iter457 — Mock fixtures + state variants for the Storybook-style
// Widget Preview page (`/dashboard/widgets/preview`). Each widget gets
// 3 deterministic data states so a designer/QA can verify how the UI
// reacts to empty / calm / busy data without touching the live tenant.
//
// State conventions:
//   empty — null or zero-length payloads, the "first-time user" view
//   calm  — small healthy dataset, the daily "happy path"
//   busy  — alert-heavy dataset that exercises every variant + warning
//
// These fixtures are intentionally hand-written (not generated) so the
// values are stable for visual diffs and screenshot regression.

import { TrendingUp, Wallet, Banknote, Building2 as BankIcon } from 'lucide-react';

export const STATE_KEYS = ['empty', 'calm', 'busy'];

// ── Stat / chart sources ─────────────────────────────────────────────
export const MOCK_STATS = {
  empty: {},
  calm: {
    total_sales: 124530.5, total_expenses: 32450, net_profit: 38120.75,
    cash_sales: 41000, bank_sales: 78000, online_sales: 5530.5,
    cash_in_hand: 89400, bank_in_hand: 412800,
    expenses_cash: 12450, expenses_bank: 20000,
    expense_by_category: { Salaries: 14000, Rent: 6000, Utilities: 3450 },
    daily_summary: Array.from({ length: 7 }, (_, i) => ({
      day: `Day ${i + 1}`, sales: 1500 + i * 120, expenses: 400 + i * 50, profit: 1000 + i * 70,
    })),
    payment_split: { cash: 41000, bank: 78000, credit: 5530 },
  },
  busy: {
    total_sales: 984310.55, total_expenses: 423500.25, net_profit: 156210.30,
    cash_sales: 290000, bank_sales: 590000, online_sales: 104310.55,
    cash_in_hand: -12400, bank_in_hand: 1240800,
    expenses_cash: 89000, expenses_bank: 334500,
    online_receivable: 38000, platform_received: 66310,
    pending_credits: 45230, sp_pct_of_sales: 23, profit_pct_of_sales: 16,
    expense_by_category: {
      Salaries: 240000, 'Cost of Goods': 95000, Rent: 32000, Utilities: 18500,
      Marketing: 22000, Maintenance: 9000, Misc: 7000,
    },
    daily_summary: Array.from({ length: 14 }, (_, i) => ({
      day: `D${i + 1}`,
      sales: 4500 + Math.sin(i / 2) * 1800 + i * 100,
      expenses: 1800 + Math.cos(i / 3) * 700,
      profit: 2700 + Math.sin(i / 2) * 1100,
    })),
    payment_split: { cash: 290000, bank: 590000, credit: 104310 },
  },
};

// ── Stat cards (Overview / Money-Movement rows) ───────────────────────
export const MOCK_STAT_CARDS = {
  empty: [],
  calm: [
    { title: 'Total Sales', numValue: 124530.5, icon: TrendingUp, color: 'text-emerald-500', bgColor: 'bg-emerald-50', testId: 'preview-stat-sales', pct: 12.4, sparkline: [3,5,4,6,7,5,8,9,7,10,12,11,14], sparkColor: '#22C55E' },
    { title: 'Net Profit', numValue: 38120.75, icon: Wallet, color: 'text-blue-500', bgColor: 'bg-blue-50', testId: 'preview-stat-profit', pct: 0, sparkline: [2,3,2,4,5,3,5], sparkColor: '#3B82F6' },
    { title: 'Cash Balance', numValue: 89400, icon: Banknote, color: 'text-amber-500', bgColor: 'bg-amber-50', testId: 'preview-stat-cash', pct: 5.2 },
    { title: 'Bank Balance', numValue: 412800, icon: BankIcon, color: 'text-violet-500', bgColor: 'bg-violet-50', testId: 'preview-stat-bank', pct: 0 },
  ],
  busy: [
    { title: 'Total Sales', numValue: 984310.55, icon: TrendingUp, color: 'text-emerald-500', bgColor: 'bg-emerald-50', testId: 'preview-stat-sales', pct: 38.2, sparkline: [3,5,4,6,7,5,8,12,15,18,22,28,34,42], sparkColor: '#22C55E' },
    { title: 'Net Profit', numValue: 156210.30, icon: Wallet, color: 'text-blue-500', bgColor: 'bg-blue-50', testId: 'preview-stat-profit', pct: -12.1, sparkline: [10,12,8,15,18,10,7,4], sparkColor: '#3B82F6' },
    { title: 'Cash Balance', numValue: -12400, icon: Banknote, color: 'text-rose-500', bgColor: 'bg-rose-50', testId: 'preview-stat-cash', pct: -23.6 },
    { title: 'Bank Balance', numValue: 1240800, icon: BankIcon, color: 'text-violet-500', bgColor: 'bg-violet-50', testId: 'preview-stat-bank', pct: 8.4 },
  ],
};

// ── Bank Account Summary ─────────────────────────────────────────────
export const MOCK_BANK_SUMMARY = {
  empty: { accounts: [], total_bank_incoming: 0, total_bank_outgoing: 0, total_bank_net: 0 },
  calm: {
    accounts: [
      { id: 'b1', name: 'Alinma Main', assigned_branch: 'HQ', is_default: true,
        incoming: 124000, outgoing: 65000, net: 59000, sales_count: 124 },
      { id: 'b2', name: 'Al Bilad – Branch 2', assigned_branch: 'Branch 2',
        incoming: 38000, outgoing: 22000, net: 16000, sales_count: 41 },
    ],
    total_bank_incoming: 162000, total_bank_outgoing: 87000, total_bank_net: 75000,
  },
  busy: {
    accounts: [
      { id: 'b1', name: 'Alinma Main', assigned_branch: 'HQ', is_default: true,
        incoming: 1416865, outgoing: 1457120, net: -40254, sales_count: 2895 },
      { id: 'b2', name: 'Al Bilad – Branch 2', assigned_branch: 'Branch 2',
        incoming: 768280, outgoing: 748038, net: 20242, sales_count: 2037 },
      { id: 'b3', name: 'Al Rajhi – Branch 3', assigned_branch: 'Branch 3',
        incoming: 240000, outgoing: 195000, net: 45000, sales_count: 510 },
    ],
    total_bank_incoming: 2425145, total_bank_outgoing: 2400158, total_bank_net: 24987,
  },
};

// ── Recent Transactions ──────────────────────────────────────────────
export const MOCK_RECENT_TXNS = {
  empty: [],
  calm: [
    { date: '2026-02-10T09:30:00', txType: 'sale', invoice_number: 'INV-1042', total: 245.5, branch_name: 'HQ' },
    { date: '2026-02-10T08:15:00', txType: 'expense', category: 'Fuel', amount: 120, description: 'Petrol – delivery van' },
    { date: '2026-02-09T18:42:00', txType: 'sale', invoice_number: 'INV-1041', total: 890.0, branch_name: 'Branch 2' },
    { date: '2026-02-09T11:00:00', txType: 'expense', category: 'Supplies', amount: 340, description: 'Cleaning supplies' },
  ],
  busy: Array.from({ length: 8 }, (_, i) => i % 2 === 0
    ? { date: `2026-02-${10 - Math.floor(i / 2)}T${20 - i}:00:00`, txType: 'sale',
        invoice_number: `INV-${2100 + i}`, total: 1240 + i * 95, branch_name: ['HQ', 'B2', 'B3'][i % 3] }
    : { date: `2026-02-${10 - Math.floor(i / 2)}T${20 - i}:00:00`, txType: 'expense',
        category: ['Salaries', 'Rent', 'Marketing', 'Fuel'][i % 4],
        amount: 850 + i * 60, description: `Auto-generated expense #${i}` }
  ),
};

// ── Dues / Salaries / Branch dues ────────────────────────────────────
export const MOCK_PENDING_SALARIES = {
  empty: { employees: [], branch_summary: {}, totals: { total_salary: 0, total_paid: 0, total_pending: 0 }, period: '' },
  calm: { employees: [{ id: 'e1', name: 'Aisha', pending: 1200 }],
    branch_summary: { HQ: { pending: 1200 } },
    totals: { total_salary: 12000, total_paid: 10800, total_pending: 1200 }, period: '2026-02' },
  busy: { employees: Array.from({ length: 12 }, (_, i) => ({ id: `e${i}`, name: `Emp ${i + 1}`, pending: 1500 + i * 220 })),
    branch_summary: { HQ: { pending: 8400 }, B2: { pending: 5200 }, B3: { pending: 3100 } },
    totals: { total_salary: 145000, total_paid: 128300, total_pending: 16700 }, period: '2026-02' },
};

export const MOCK_BRANCH_DUES = {
  empty: [],
  calm: [{ branch_name: 'HQ', net_due: 1200 }, { branch_name: 'B2', net_due: -380 }],
  busy: [
    { branch_name: 'HQ', net_due: 12400 }, { branch_name: 'B2', net_due: -2800 },
    { branch_name: 'B3', net_due: 8900 }, { branch_name: 'B4', net_due: -1450 },
  ],
};

// ── Budget alerts ────────────────────────────────────────────────────
export const MOCK_BUDGET_ALERTS = {
  empty: { total_active: 0, counts: {}, alerts: [] },
  calm: { total_active: 1, counts: { warning: 1, breach: 0, on_track: 5 },
    alerts: [{ category: 'Marketing', severity: 'warning', spent: 8200, budget: 10000, period: 'Feb' }] },
  busy: { total_active: 4, counts: { warning: 2, breach: 2, on_track: 3 },
    alerts: [
      { category: 'Marketing', severity: 'breach', spent: 12400, budget: 10000, period: 'Feb' },
      { category: 'Maintenance', severity: 'breach', spent: 5800, budget: 4000, period: 'Feb' },
      { category: 'Office', severity: 'warning', spent: 3500, budget: 4000, period: 'Feb' },
      { category: 'Travel', severity: 'warning', spent: 1800, budget: 2000, period: 'Feb' },
    ] },
};

// ── Dunning Scorecard ────────────────────────────────────────────────
export const MOCK_DUNNING = {
  empty: null,
  calm: { score: 92, grade: 'A', overdue_count: 1, overdue_amount: 1200, dso_days: 18,
    summary_30d: { invoices_sent: 24, paid_on_time: 22, escalated: 1 } },
  busy: { score: 48, grade: 'D', overdue_count: 18, overdue_amount: 84200, dso_days: 62,
    summary_30d: { invoices_sent: 142, paid_on_time: 84, escalated: 18 } },
};

// ── Stock Alert Trends ───────────────────────────────────────────────
export const MOCK_STOCK_TRENDS = {
  empty: { trend: [], top_items: [], summary: {} },
  calm: { trend: Array.from({ length: 7 }, (_, i) => ({ date: `Day ${i + 1}`, low_stock: i + 1, out_of_stock: 0 })),
    top_items: [{ name: 'Sugar 1kg', stock: 4, reorder_at: 10 }],
    summary: { total_alerts: 7, critical: 0, low: 7 } },
  busy: { trend: Array.from({ length: 30 }, (_, i) => ({ date: `D${i + 1}`, low_stock: 8 + (i % 7), out_of_stock: i % 4 })),
    top_items: Array.from({ length: 6 }, (_, i) => ({ name: ['Sugar', 'Rice', 'Oil', 'Flour', 'Milk', 'Eggs'][i] + ' 1kg', stock: i, reorder_at: 10 })),
    summary: { total_alerts: 38, critical: 6, low: 32 } },
};

// ── Anomaly Detection (history list) ─────────────────────────────────
export const MOCK_ANOMALY = {
  empty: [],
  calm: [{ scan_date: '2026-02-10', critical: 0, warnings: 1, summary: 'All systems normal — one mild outlier in Branch 2 supplies spend.' }],
  busy: [{ scan_date: '2026-02-10', critical: 1, warnings: 13, summary: 'Duplicate invoice INV-1042 detected · Branch 3 expenses 4σ above mean · 11 minor variances.' }],
};

// ── Predictive AI ────────────────────────────────────────────────────
export const MOCK_PREDICTIVE = {
  empty: {
    lowStock: { items_at_risk: 0, forecasts: [] },
    peakHours: { peak_hours: [], total_transactions_analyzed: 0 },
    customerCLV: { high_value_customers: [], total_projected_revenue: 0 },
    profitTrend: { summary: {}, daily_breakdown: [] },
  },
  calm: {
    lowStock: { items_at_risk: 2, forecasts: [{ name: 'Sugar', days_left: 5 }, { name: 'Rice', days_left: 8 }] },
    peakHours: { peak_hours: [{ hour: 13, transactions: 24 }, { hour: 19, transactions: 38 }], total_transactions_analyzed: 412 },
    customerCLV: { high_value_customers: [{ name: 'Cafe Latte', projected_value: 12400 }], total_projected_revenue: 12400 },
    profitTrend: { summary: { total: 38120, trend_pct: 12 },
      daily_breakdown: Array.from({ length: 7 }, (_, i) => ({ day: `D${i + 1}`, profit: 4000 + i * 250 })) },
  },
  busy: {
    lowStock: { items_at_risk: 18, forecasts: Array.from({ length: 6 }, (_, i) => ({ name: ['Sugar', 'Rice', 'Oil', 'Flour', 'Milk', 'Eggs'][i], days_left: i })) },
    peakHours: { peak_hours: Array.from({ length: 6 }, (_, i) => ({ hour: 11 + i, transactions: 80 + i * 15 })), total_transactions_analyzed: 4120 },
    customerCLV: { high_value_customers: Array.from({ length: 5 }, (_, i) => ({ name: `Customer ${i + 1}`, projected_value: 12400 + i * 8000 })),
      total_projected_revenue: 184200 },
    profitTrend: { summary: { total: 156210, trend_pct: -8 },
      daily_breakdown: Array.from({ length: 30 }, (_, i) => ({ day: `D${i + 1}`, profit: 5000 + Math.sin(i / 4) * 2200 })) },
  },
};

// ── Branches list (Inter-branch widget) ──────────────────────────────
export const MOCK_BRANCHES = {
  empty: [],
  calm: [{ id: 'h1', name: 'HQ' }, { id: 'b2', name: 'Branch 2' }],
  busy: [{ id: 'h1', name: 'HQ' }, { id: 'b2', name: 'Branch 2' }, { id: 'b3', name: 'Branch 3' }, { id: 'b4', name: 'Branch 4' }],
};

// ── Payment-mode cards (Money Movement row) ──────────────────────────
export const MOCK_PAYMENT_MODE_CARDS = {
  empty: [],
  calm: [
    { title: 'Cash in Hand', numValue: 89400, icon: Banknote, color: 'text-amber-500', bgColor: 'bg-amber-50', testId: 'preview-pm-cash' },
    { title: 'Bank Balance', numValue: 412800, icon: BankIcon, color: 'text-violet-500', bgColor: 'bg-violet-50', testId: 'preview-pm-bank' },
  ],
  busy: [
    { title: 'Cash in Hand', numValue: -12400, icon: Banknote, color: 'text-rose-500', bgColor: 'bg-rose-50', testId: 'preview-pm-cash' },
    { title: 'Bank Balance', numValue: 1240800, icon: BankIcon, color: 'text-violet-500', bgColor: 'bg-violet-50', testId: 'preview-pm-bank' },
    { title: 'Online Receivable', numValue: 38000, icon: Wallet, color: 'text-blue-500', bgColor: 'bg-blue-50', testId: 'preview-pm-online' },
  ],
};
